import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Building2, Home, Image, FileText } from 'lucide-react';

interface Step5Props {
  data: any;
  onBack: () => void;
  isEdit?: boolean;
  promotionId?: string;
}

const Step5Recap: React.FC<Step5Props> = ({ data, onBack, isEdit = false, promotionId }) => {
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publishOptions, setPublishOptions] = useState({
    status: 'draft' as 'draft' | 'published',
    publish_dar_dz: false,
    publish_ouedkniss: false,
  });

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);

    try {
      const promotionData = {
        agency_id: 1,
        created_by_user_id: 1,
        name: data.name,
        address: data.address,
        city: data.city,
        wilaya: data.wilaya,
        promoter_name: data.promoter_name,
        promoter_phone: data.promoter_phone,
        promoter_email: data.promoter_email,
        delivery_date: data.delivery_date,
        construction_progress: data.construction_progress,
        description: data.description,
        photos: JSON.stringify(data.photos || []),
        master_plan_url: data.master_plan_url,
        brochure_url: data.brochure_url,
        status: publishOptions.status,
        is_published: publishOptions.status === 'published' ? 1 : 0,
        publish_dar_dz: publishOptions.publish_dar_dz ? 1 : 0,
        publish_ouedkniss: publishOptions.publish_ouedkniss ? 1 : 0,
      };

      let finalPromotionId: string;

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

      if (isEdit && promotionId) {
        const promoResponse = await fetch(`${apiUrl}/promotions/${promotionId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(promotionData),
        });

        if (!promoResponse.ok) throw new Error('Erreur mise à jour promotion');
        finalPromotionId = promotionId;
      } else {
        const promoResponse = await fetch(`${apiUrl}/promotions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(promotionData),
        });

        if (!promoResponse.ok) throw new Error('Erreur création promotion');

        const { id } = await promoResponse.json();
        finalPromotionId = id;
      }

      const blocksResponse = await fetch(`${apiUrl}/promotions/${finalPromotionId}/blocks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blocks: data.blocks }),
      });

      if (!blocksResponse.ok) throw new Error('Erreur création blocs');

      const { blocks: createdBlocks } = await blocksResponse.json();

      for (let i = 0; i < data.blocks.length; i++) {
        const block = data.blocks[i];
        const createdBlock = createdBlocks[i];

        const blockUnits = data.units.filter((u: any) => u.block_name === block.block_name);

        if (blockUnits.length > 0) {
          const unitsResponse = await fetch(`${apiUrl}/blocks/${createdBlock.id}/units`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ units: blockUnits }),
          });

          if (!unitsResponse.ok) throw new Error(`Erreur création lots bloc ${block.block_name}`);
        }
      }

      for (const [type, photos] of Object.entries(data.typePhotos || {})) {
        if ((photos as string[]).length > 0) {
          await fetch(`${apiUrl}/promotions/${finalPromotionId}/type-photos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              unit_type: type,
              photos: JSON.stringify(photos),
            }),
          });
        }
      }

      navigate(`/promotions/${finalPromotionId}`);

    } catch (err: any) {
      console.error('Erreur publication:', err);
      setError(err.message || 'Une erreur est survenue lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Récapitulatif
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Vérifiez les informations avant de {isEdit ? 'modifier' : 'créer'} la promotion
        </p>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 space-y-4">
        <div>
          <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-3">
            <Building2 className="w-5 h-5 mr-2" />
            {data.name}
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Adresse :</span>
              <p className="text-gray-900 dark:text-white font-medium">{data.address}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Ville :</span>
              <p className="text-gray-900 dark:text-white font-medium">{data.city}, {data.wilaya}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Promoteur :</span>
              <p className="text-gray-900 dark:text-white font-medium">{data.promoter_name || 'Non renseigné'}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Livraison :</span>
              <p className="text-gray-900 dark:text-white font-medium">
                {data.delivery_date ? new Date(data.delivery_date).toLocaleDateString('fr-FR') : 'Non renseignée'}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="flex items-center font-semibold text-gray-900 dark:text-white mb-2">
            <Home className="w-4 h-4 mr-2" />
            {data.blocks?.length || 0} bloc(s)
          </h4>
          <div className="grid grid-cols-3 gap-2">
            {data.blocks?.map((block: any, index: number) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded p-2 text-sm">
                <p className="font-medium text-gray-900 dark:text-white">Bloc {block.block_name}</p>
                <p className="text-gray-600 dark:text-gray-400 text-xs">{block.total_floors} étages</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
            {data.units?.length || 0} lot(s) généré(s)
          </h4>
          <div className="grid grid-cols-4 gap-2 text-sm">
            {['f2', 'f3', 'f4', 'f5'].map(type => {
              const count = data.units?.filter((u: any) => u.unit_type === type).length || 0;
              return count > 0 ? (
                <div key={type} className="bg-white dark:bg-gray-700 rounded p-2 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">{type}</p>
                </div>
              ) : null;
            })}
          </div>
        </div>

        <div>
          <h4 className="flex items-center font-semibold text-gray-900 dark:text-white mb-2">
            <Image className="w-4 h-4 mr-2" />
            {data.photos?.length || 0} photo(s)
          </h4>
          {data.photos && data.photos.length > 0 && (
            <div className="flex space-x-2 overflow-x-auto">
              {data.photos.slice(0, 5).map((url: string, index: number) => (
                <img
                  key={index}
                  src={url}
                  alt={`Photo ${index + 1}`}
                  className="w-20 h-20 object-cover rounded"
                />
              ))}
              {data.photos.length > 5 && (
                <div className="w-20 h-20 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
                  +{data.photos.length - 5}
                </div>
              )}
            </div>
          )}
        </div>

        {(data.master_plan_url || data.brochure_url) && (
          <div>
            <h4 className="flex items-center font-semibold text-gray-900 dark:text-white mb-2">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </h4>
            <div className="space-y-1 text-sm">
              {data.master_plan_url && (
                <p className="text-gray-600 dark:text-gray-400">Plan de masse</p>
              )}
              {data.brochure_url && (
                <p className="text-gray-600 dark:text-gray-400">Brochure PDF</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="border dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Options de publication
        </h3>

        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={publishOptions.status === 'draft'}
                onChange={() => setPublishOptions({ ...publishOptions, status: 'draft' })}
                className="mr-2"
              />
              <span className="text-gray-900 dark:text-white">
                Enregistrer en brouillon
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="radio"
                checked={publishOptions.status === 'published'}
                onChange={() => setPublishOptions({ ...publishOptions, status: 'published' })}
                className="mr-2"
              />
              <span className="text-gray-900 dark:text-white">
                Publier maintenant
              </span>
            </label>
          </div>

          {publishOptions.status === 'published' && (
            <div className="space-y-2 ml-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={publishOptions.publish_dar_dz}
                  onChange={(e) => setPublishOptions({ ...publishOptions, publish_dar_dz: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-gray-900 dark:text-white">
                  Publier sur dar-dz.com
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={publishOptions.publish_ouedkniss}
                  onChange={(e) => setPublishOptions({ ...publishOptions, publish_ouedkniss: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-gray-900 dark:text-white">
                  Publier sur Ouedkniss
                </span>
              </label>
            </div>
          )}
        </div>

        {publishOptions.status === 'published' && (
          <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-500 p-4">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              En publiant, tous les lots "disponibles" seront visibles publiquement
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-500 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-red-900 dark:text-red-100 mb-1">Erreur</h4>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-6 border-t dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          disabled={publishing}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          Retour
        </button>

        <button
          onClick={handlePublish}
          disabled={publishing}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
        >
          {publishing ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Création en cours...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              <span>
                {isEdit
                  ? (publishOptions.status === 'draft' ? 'Enregistrer les modifications' : 'Mettre à jour la promotion')
                  : (publishOptions.status === 'draft' ? 'Enregistrer le brouillon' : 'Créer la promotion')
                }
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step5Recap;
